import React from "react";
import { Accordion } from "react-bootstrap";

function getDkScarfZoneRanges(rule) {
  if (!rule) return null;
  const left1 = Number(rule.Left1Count) || 0;
  const left2 = Number(rule.Left2Count) || 0;
  const middle = Number(rule.MiddleCount) || 0;
  const right2 = Number(rule.Right2Count) || 0;
  const right1 = Number(rule.Right1Count) || 0;
  let i = 0;
  const ranges = {
    left1: { start: i, end: (i += left1) },
    left2: { start: i, end: (i += left2) },
    middle: { start: i, end: (i += middle) },
    right2: { start: i, end: (i += right2) },
    right1: { start: i, end: (i += right1) },
  };
  return { ranges, total: Number(rule.TotalCount) || left1 + left2 + middle + right2 + right1 };
}

function getZoneIndices(zones, ranges) {
  const indices = [];
  if (!ranges || !zones) return indices;
  zones.forEach((zoneName) => {
    const range = ranges[zoneName];
    if (!range) return;
    for (let idx = range.start; idx < range.end; idx += 1) indices.push(idx);
  });
  return indices;
}

function normalizeDkFabricEntry(entry) {
  if (!entry || !Object.keys(entry).length) return null;
  return entry.FabricObj && Object.keys(entry.FabricObj).length ? entry.FabricObj : entry;
}

function getFirstFabricInZones(zones, ranges, curtainArr) {
  const indices = getZoneIndices(zones, ranges);
  for (let i = 0; i < indices.length; i += 1) {
    const fabric = normalizeDkFabricEntry(curtainArr?.[indices[i]]);
    if (fabric) return fabric;
  }
  return null;
}

function getSodFabricsSorted(obj) {
  return [...(obj?.SodFabrics || [])].sort(
    (a, b) => Number(a.FabricOrder || 0) - Number(b.FabricOrder || 0)
  );
}

function sodFabricLabelAt(obj, index, pageLanguage) {
  const item = getSodFabricsSorted(obj)[index];
  if (!item) return "";
  const candidates = [
    normalizeDkFabricEntry(item),
    item?.FabricObj,
    item,
  ].filter(Boolean);
  for (let i = 0; i < candidates.length; i += 1) {
    const label = dkFabricColorLabel(candidates[i], pageLanguage);
    if (label) return label;
  }
  return "";
}

export function dkFabricColorLabel(fabric, lang) {
  if (!fabric) return "";
  const design =
    lang === "fa"
      ? fabric.DesignName || fabric.FabricName
      : fabric.DesignEnName || fabric.FabricEnName;
  const color = lang === "fa" ? fabric.ColorName : fabric.ColorEnName;
  const designText = design ? String(design).trim() : "";
  const colorText = color ? String(color).trim() : "";
  if (!designText && !colorText) return "";
  const label =
    designText && colorText ? `${designText} / ${colorText}` : designText || colorText;
  return lang === "en"
    ? label.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase())
    : label;
}

function isDkMonoFabricMode(obj) {
  if (obj?.statusFabricItem === "multi" || obj?.statusFabricItem === "dual") {
    return false;
  }
  if (obj?.statusFabricItem === "mono") return true;
  return getSodFabricsSorted(obj).length === 1;
}

/** Resolve single-fabric (mono) label for checkout / logged-in cart when API omits nameFabricItem. */
export function resolveDkMonoFabricLabel(obj, pageLanguage, t) {
  const named =
    pageLanguage === "fa" ? obj?.nameFabricItemFa : obj?.nameFabricItemEn;
  if (named && String(named).trim() && !String(named).includes("undefined")) {
    return String(named).trim();
  }

  const fromSod = sodFabricLabelAt(obj, 0, pageLanguage);
  if (fromSod && fromSod !== t("dk_select_fabric")) return fromSod;

  const fromFabricId = findDkFabricLabelById(obj, obj?.FabricId, pageLanguage);
  if (fromFabricId) return fromFabricId;

  if (obj?.selectedTextEn || obj?.selectedTextFa) {
    return pageLanguage === "fa"
      ? `${obj.selectedTextFa || ""} / ${obj.selectedColorFa || ""}`.replace(/\s*\/\s*$/, "").trim()
      : dkFabricColorLabel(
          { DesignEnName: obj.selectedTextEn, ColorEnName: obj.selectedColorEn },
          pageLanguage
        );
  }

  const fromCurtain = getUniqueDkFabricLabels(obj, pageLanguage)[0];
  if (fromCurtain) return fromCurtain;

  const design =
    pageLanguage === "fa" ? obj?.FabricDesignFa : obj?.FabricDesignEn;
  const color = pageLanguage === "fa" ? obj?.FabricColorFa : obj?.FabricColorEn;
  if (design || color) {
    return dkFabricColorLabel(
      {
        DesignName: obj?.FabricDesignFa,
        DesignEnName: obj?.FabricDesignEn,
        ColorName: obj?.FabricColorFa,
        ColorEnName: obj?.FabricColorEn,
      },
      pageLanguage
    );
  }

  const placementLabel = dkPlacementColorText(
    obj,
    ["left1", "left2", "right2", "right1", "middle"],
    pageLanguage,
    t
  );
  if (placementLabel && placementLabel !== t("dk_select_fabric")) {
    return placementLabel;
  }

  return "";
}

function getUniqueDkFabricLabels(obj, pageLanguage) {
  const labels = [];
  const seen = new Set();
  for (const entry of obj?.CurtainArr || []) {
    const fabric = normalizeDkFabricEntry(entry);
    const key = fabric?.FabricId || `${fabric?.DesignEnName}-${fabric?.ColorEnName}`;
    if (!fabric || !key || seen.has(key)) continue;
    seen.add(key);
    const label = dkFabricColorLabel(fabric, pageLanguage);
    if (label) labels.push(label);
  }
  return labels;
}

function findDkFabricLabelById(obj, fabricId, pageLanguage) {
  if (fabricId === undefined || fabricId === null || fabricId === "") return "";
  const id = String(fabricId);
  for (const entry of [...getSodFabricsSorted(obj), ...(obj?.CurtainArr || [])]) {
    const candidates = [normalizeDkFabricEntry(entry), entry?.FabricObj, entry].filter(Boolean);
    for (let i = 0; i < candidates.length; i += 1) {
      const fabric = candidates[i];
      if (fabric && String(fabric.FabricId) === id) {
        const label = dkFabricColorLabel(fabric, pageLanguage);
        if (label) return label;
      }
    }
  }
  return "";
}

function dkPlacementColorText(obj, zones, pageLanguage, t) {
  const ranges = getDkScarfZoneRanges(obj?.DkScarfRule)?.ranges;
  if (ranges && obj?.CurtainArr?.length) {
    const fabric = getFirstFabricInZones(zones, ranges, obj.CurtainArr);
    const name = dkFabricColorLabel(fabric, pageLanguage);
    if (name) return name;
  }

  const status = obj?.statusFabricItem;
  const isMiddleOnly = zones.length === 1 && zones[0] === "middle";
  const isSides =
    zones.includes("left1") &&
    zones.includes("left2") &&
    zones.includes("right2") &&
    zones.includes("right1");
  const isOuter = zones.includes("left1") && zones.includes("right1") && !zones.includes("left2");
  const isInner = zones.includes("left2") && zones.includes("right2");

  if (status === "mono") {
    const name = sodFabricLabelAt(obj, 0, pageLanguage);
    if (name) return name;
  }

  if (status === "multi") {
    const name = sodFabricLabelAt(obj, isMiddleOnly ? 1 : 0, pageLanguage);
    if (name) return name;
  }
  if (status === "dual") {
    const sodIndex = isOuter ? 0 : isInner ? 1 : isMiddleOnly ? 2 : -1;
    if (sodIndex >= 0) {
      const name = sodFabricLabelAt(obj, sodIndex, pageLanguage);
      if (name) return name;
    }
  }

  if (isSides) {
    const name = sodFabricLabelAt(obj, 0, pageLanguage);
    if (name) return name;
  }

  const uniqueLabels = getUniqueDkFabricLabels(obj, pageLanguage);
  if (status === "multi") {
    if (isMiddleOnly && uniqueLabels[1]) return uniqueLabels[1];
    if (isSides && uniqueLabels[0]) return uniqueLabels[0];
  }
  if (status === "dual") {
    const sodIndex = isOuter ? 0 : isInner ? 1 : isMiddleOnly ? 2 : -1;
    if (sodIndex >= 0 && uniqueLabels[sodIndex]) return uniqueLabels[sodIndex];
    if (isMiddleOnly) {
      const centerById = findDkFabricLabelById(obj, obj?.FabricId, pageLanguage);
      if (centerById) return centerById;
    }
  }
  if (uniqueLabels[0]) return uniqueLabels[0];

  return t("dk_select_fabric");
}

export function mergeDkFabricCartObj(preorder, draperyRow) {
  const detailRow =
    draperyRow?.SewingOrderDetails?.[0] || preorder?.SewingOrderDetails?.[0] || null;
  return {
    ...preorder,
    SewingModelId: preorder?.SewingModelId ?? draperyRow?.SewingModelId,
    FabricId: preorder?.FabricId ?? draperyRow?.FabricId ?? detailRow?.FabricId,
    CurtainArr: preorder?.CurtainArr ?? draperyRow?.CurtainArr,
    DkScarfRule: preorder?.DkScarfRule ?? draperyRow?.DkScarfRule,
    SodFabrics:
      preorder?.SodFabrics ?? draperyRow?.SodFabrics ?? detailRow?.SodFabrics,
    statusFabricItem:
      preorder?.statusFabricItem ?? draperyRow?.statusFabricItem ?? detailRow?.statusFabricItem,
    nameFabricItemFa:
      preorder?.nameFabricItemFa ??
      draperyRow?.nameFabricItemFa ??
      detailRow?.nameFabricItemFa,
    nameFabricItemEn:
      preorder?.nameFabricItemEn ??
      draperyRow?.nameFabricItemEn ??
      detailRow?.nameFabricItemEn,
    selectedTextFa: preorder?.selectedTextFa ?? draperyRow?.selectedTextFa,
    selectedTextEn: preorder?.selectedTextEn ?? draperyRow?.selectedTextEn,
    selectedColorFa: preorder?.selectedColorFa ?? draperyRow?.selectedColorFa,
    selectedColorEn: preorder?.selectedColorEn ?? draperyRow?.selectedColorEn,
    FabricDesignFa: preorder?.FabricDesignFa ?? draperyRow?.FabricDesignFa,
    FabricDesignEn: preorder?.FabricDesignEn ?? draperyRow?.FabricDesignEn,
    FabricColorFa: preorder?.FabricColorFa ?? draperyRow?.FabricColorFa,
    FabricColorEn: preorder?.FabricColorEn ?? draperyRow?.FabricColorEn,
  };
}

export function dkCheckoutFabricDetailRows(obj, pageLanguage, t) {
  const status = obj?.statusFabricItem;
  const placementColorText = (zones) => dkPlacementColorText(obj, zones, pageLanguage, t);

  if (status === "mono" || isDkMonoFabricMode(obj)) {
    const resolved = resolveDkMonoFabricLabel(obj, pageLanguage, t);
    if (!resolved) return [];
    return [{ label: t("Fabric/Color"), value: resolved }];
  }

  if (status === "multi") {
    return [
      {
        label: t("dk_checkout_side_panels_fabric"),
        value: placementColorText(["left1", "left2", "right2", "right1"]),
      },
      {
        label: t("dk_checkout_center_panels_fabric"),
        value: placementColorText(["middle"]),
      },
    ];
  }

  if (status === "dual") {
    return [
      {
        label: t("dk_checkout_outer_sides_fabric"),
        value: placementColorText(["left1", "right1"]),
      },
      {
        label: t("dk_checkout_inner_sides_fabric"),
        value: placementColorText(["left2", "right2"]),
      },
      {
        label: t("dk_checkout_center_fabric"),
        value: placementColorText(["middle"]),
      },
    ];
  }

  if (obj?.SewingModelId === "0326" && status !== "multi" && status !== "dual") {
    const resolved = resolveDkMonoFabricLabel(obj, pageLanguage, t);
    if (resolved) {
      return [{ label: t("Fabric/Color"), value: resolved }];
    }
  }

  return [];
}

/** Checkout View Details — DK fabric rows at top of expanded details. */
export function DkCheckoutFabricDetails({ product, pageLanguage, t }) {
  const pre = product?.PreorderText || {};
  const obj = mergeDkFabricCartObj(pre, { ...pre, ...product });
  const rows = dkCheckoutFabricDetailRows(obj, pageLanguage, t);
  if (!rows.length) return null;

  return (
    <>
      {rows.map(({ label, value }, index) => (
        <div
          key={`${label}-${index}`}
          className={`lable_Details ${
            pageLanguage === "fa" ? `lable_Details_rtl dk_checkout_fabric_${index}` : ""
          }`}
        >
          <p>{label}</p>
          <span>{value}</span>
        </div>
      ))}
    </>
  );
}

function dkSideCartFabricRows(obj, pageLanguage, t) {
  const statusFabricItem = obj["statusFabricItem"];
  const placementColorText = (zones) => dkPlacementColorText(obj, zones, pageLanguage, t);

  if (statusFabricItem === "multi") {
    return [
      { title: t("dk_side_panels"), zones: ["left1", "left2", "right2", "right1"] },
      { title: t("dk_center_panels"), zones: ["middle"] },
    ];
  }

  if (statusFabricItem === "dual") {
    return [
      { title: t("dk_outer_sides"), zones: ["left1", "right1"] },
      { title: t("dk_inner_sides"), zones: ["left2", "right2"] },
      { title: t("dk_center"), zones: ["middle"] },
    ];
  }

  return [];
}

export function DkSideCartFabricAccordionBody({ obj, pageLanguage, t }) {
  const rows = dkSideCartFabricRows(obj, pageLanguage, t);
  const placementColorText = (zones) => dkPlacementColorText(obj, zones, pageLanguage, t);

  return (
    <>
      {rows.map(({ title, zones }, index) => (
        <p
          key={`${title}-${index}`}
          className={`cart_agree_item_desc cart_agree_item_desc_dk ${6 + index}`}
          style={{ margin: 0 }}
        >
          <span className="dk_placement_title">{title}: </span>
          <span className="dk_placement_sub dk_placement_sub_set">{placementColorText(zones)}</span>
        </p>
      ))}
    </>
  );
}

/** Side cart (My Bag) only — fabrics shown inline, no View Details accordion. */
export function DkSideCartFabricMiniCart({ obj, pageLanguage, t }) {
  const statusFabricItem = obj["statusFabricItem"];

  if (statusFabricItem === "mono") {
    const label = resolveDkMonoFabricLabel(obj, pageLanguage, t);
    return (
      <div className="custom_cart_item_desc_container custom_cart_item_desc_container_fabrics dk_mini">
        <h2 className="custom_cart_item_desc_detail">{label}</h2>
      </div>
    );
  }

  const rows = dkSideCartFabricRows(obj, pageLanguage, t);
  const placementColorText = (zones) => dkPlacementColorText(obj, zones, pageLanguage, t);

  return (
    <>
      {rows.map(({ title, zones }, index) => (
        <div
          key={`${title}-${index}`}
          className="custom_cart_item_desc_container custom_cart_item_desc_container_fabrics dk_mini"
        >
          <h2 className="custom_cart_item_desc_detail">
            <span className="dk_placement_title">{title}: </span>
            {placementColorText(zones)}
          </h2>
        </div>
      ))}
    </>
  );
}

export function DkMultiFabricAccordion({
  obj,
  pageLanguage,
  t,
  containerClassName = "dk_curtain_preview_container",
  detailContainerClassName = "dk_curtain_preview_detail_container",
  ToggleComponent,
}) {
  return (
    <div className={containerClassName}>
      <Accordion>
        <Accordion.Item eventKey="0">
          <ToggleComponent eventKey="0" textOnHide={t("View Details")} textOnShow={t("Hide Details")} />
          <Accordion.Body className="basket_item_title_dropdown dk_curtain_preview_dropdown">
            <div className={detailContainerClassName}>
              <DkSideCartFabricAccordionBody obj={obj} pageLanguage={pageLanguage} t={t} />
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
}
